import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Address } from '@liberty-financial/agp-shared';
import * as _ from 'lodash';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ApiHttp } from '@core/api/api-http';
import { OpportunityApiHttp } from '@core/api/opportunity-api-http';
import { MonitoringService } from '@core/services/monitoring.service';
import { MonitoringEvents } from '@core/services/monitoring-events.constants';
import { OpportunityListItem } from '@features/opportunities/models/opportunity';
import { OpportunitySummary } from '@features/opportunities/services/opportunity-summary.contract';
import { RangePeriod } from '@shared/enums/range-period.enum';
import { DatesUtil } from '@shared/utilities/dates/dates.util';

import {
  OpportunityAddressSchemaContract,
  opportunityAddressSchemaContractToAddress,
  SparkAddressUtil
} from './addressContract';
import { flattenCollection } from './contract';
import { OpportunitySearchFilterOptions, toApiStatusValue } from './opportunity-search-filter-options';

function determineFromDate(period: string): Date | null {
  const currentDate = DatesUtil.getToday();

  switch (period) {
    case RangePeriod.Last7Days:
      return DatesUtil.addDays(currentDate, -7);

    case RangePeriod.Last14Days:
      return DatesUtil.addDays(currentDate, -14);

    case RangePeriod.Last30Days:
      return DatesUtil.addMonths(currentDate, -1);

    case RangePeriod.Last6Months:
      return DatesUtil.addMonths(currentDate, -6);

    case RangePeriod.Last12Months:
      return DatesUtil.addYears(currentDate, -1);

    default:
      return null;
  }
}

@Injectable({
  providedIn: 'root'
})
export class OpportunityService {
  constructor(
    private http: OpportunityApiHttp,
    private monitoringService: MonitoringService
  ) {}

  list(
    options: OpportunitySearchFilterOptions
  ): Observable<OpportunityListItem[]> {
    let httpOptions = ApiHttp.getStandardOptions();
    const params = {
      search: options.search || '',
      type: options.type || '',
      createdDateFrom: DatesUtil.toUtcStartOfDay(options.createdDateFrom) || '',
      createdDateTo: DatesUtil.toUtcEndOfDay(options.createdDateTo) || '',
      status: toApiStatusValue(options.status),
      ownedBy: options.ownedBy?.trim() ?? '',
      tagIds: options.tagId?.trim() || ''
    };
    httpOptions = {
      headers: ApiHttp.getStandardOptions().headers,
      params
    };

    return this.http.get('opportunities', httpOptions);
  }

  summaryDefinition(
    opportunityType: string
  ): Observable<OpportunitySummary.SchemaPropertyDefinition> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-OpportunityType': opportunityType
    });
    return this.http.get<OpportunitySummary.SchemaPropertyDefinition>(
      'opportunities/schema',
      {
        headers: headers,
        params: {
          depth: 1
        }
      }
    );
  }

  getSummaryForEdit(id: string): Observable<OpportunitySummary.Model> {
    return this.http
      .get<OpportunitySummary.SchemaContract>(
        `opportunities/${id}`,
        ApiHttp.getStandardOptions()
      )
      .pipe(map((x) => OpportunitySummary.toModel(x)));
  }

  getSummarySchema(
    id: string
  ): Observable<OpportunitySummary.SchemaPropertyDefinition> {
    return this.http.getWithFeatures<OpportunitySummary.SchemaPropertyDefinition>(
      `opportunities/${id}`,
      ApiHttp.getStandardOptionsWithMetadata(),
      OpportunityApiHttp.features(false, true)
    );
  }

  update(
    change: Partial<OpportunitySummary.Model>,
    id: string,
    opportunityType: string
  ): Observable<object> {
    // Track opportunity creation
    this.monitoringService.logEvent(MonitoringEvents.OPPORTUNITY_CREATED, {
      opportunityId: id,
      opportunityType: opportunityType
    });
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-OpportunityType': opportunityType
    });
    return this.http.put(
      `opportunities/${id}`,
      OpportunitySummary.toSchemaContract(change),
      { headers: headers }
    );
  }

  copy(
    change: OpportunitySummary.Model,
    id: string,
    from: String,
    opportunityType: string
  ): Observable<object> {
    // Track opportunity copied
    this.monitoringService.logEvent(MonitoringEvents.OPPORTUNITY_COPIED, {
      opportunityId: id,
      copiedFromOpportunityId: from,
      opportunityType: opportunityType
    });
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-OpportunityType': opportunityType
    });

    return this.http.put(
      `opportunities/${id}/copy/${from}`,
      OpportunitySummary.toSchemaContract(change),
      {
        headers: headers
      }
    );
  }

  reassign(opportunityId: string, portfolioId: string): Observable<object> {
    return this.http.post(
      `opportunities/${opportunityId}/reassign/${portfolioId}`,
      {},
      ApiHttp.getStandardOptions()
    );
  }

  reassignAll(
    opportunityIds: string[],
    portfolioId: string
  ): Observable<object> {
    return forkJoin(
      opportunityIds.map(
        (o) => this.reassign(o, portfolioId)
        // TODO: Need to show detailed error messages in UI
        // .pipe(
        // map(() => ({ opportunityId: o, success: true })),
        // catchError(() => of({ opportunityId: o, success: false })))
      )
    );
  }

  getOpportunityAddresses(id: string): Observable<Address[]> {
    return this.http
      .get(`opportunities/${id}/addresses`, ApiHttp.getStandardOptions())
      .pipe(
        catchError((err) => {
          this.monitoringService.logException(
            err instanceof Error ? err : new Error('Error retrieving addresses'),
            2, // SeverityLevel.Warning
            { error: JSON.stringify(err) }
          );
          return of([]);
        }),
        map((x) => {
          const addresses = flattenCollection<OpportunityAddressSchemaContract>(
            x
          ).reduce((r: Address[], s: OpportunityAddressSchemaContract) => {
            const a = opportunityAddressSchemaContractToAddress(s);
            if (a) {
              a.id = a.id || SparkAddressUtil.getDisplayTextFromAddress(a);
              r.push(a);
            }
            return r;
          }, [] as Address[]);
          return _.uniqBy(addresses, (a) =>
            SparkAddressUtil.getDisplayTextFromAddress(a)
          );
        })
      );
  }
}
